import type { ActionItem, KpiMetric, Newsletter, NewsletterVersion } from '@/types';
import { ALL_SECTION_ORDER } from '@/types';

const PUBLISHED_ID = 'nl-seed-0012';
const DRAFT_ID = 'nl-seed-0013';

const publishedKpis: KpiMetric[] = [
  { id: 'kpi-1', label: 'MRR', value: '$412K', target: '$450K', trend: 'up' },
  { id: 'kpi-2', label: 'Active Customers', value: '186', target: '200', trend: 'up' },
  { id: 'kpi-3', label: 'Model Uptime', value: '99.94%', target: '99.9%', trend: 'flat' },
  { id: 'kpi-4', label: 'Avg. Inference Latency', value: '212ms', target: '200ms', trend: 'down' },
];

const draftKpis: KpiMetric[] = [
  { id: 'kpi-5', label: 'MRR', value: '$438K', target: '$450K', trend: 'up' },
  { id: 'kpi-6', label: 'Active Customers', value: '193', target: '200', trend: 'up' },
  { id: 'kpi-7', label: 'Model Uptime', value: '99.97%', target: '99.9%', trend: 'up' },
  { id: 'kpi-8', label: 'Avg. Inference Latency', value: '188ms', target: '200ms', trend: 'up' },
];

export const SAMPLE_NEWSLETTERS: Newsletter[] = [
  {
    id: PUBLISHED_ID,
    title: 'PulseAI Monthly Pulse — Issue #12',
    issueNumber: 12,
    status: 'published',
    periodStart: '2026-06-01',
    periodEnd: '2026-06-30',
    kpis: publishedKpis,
    enabledSections: [...ALL_SECTION_ORDER],
    coverImage: undefined,
    currentVersion: 1,
    createdAt: '2026-06-28T09:00:00.000Z',
    updatedAt: '2026-06-30T17:20:00.000Z',
    publishedAt: '2026-06-30T17:20:00.000Z',
    sections: {
      executiveSummary:
        'June was a strong month for PulseAI: MRR grew 6% month-over-month and we shipped the long-awaited **streaming inference API**. Customer churn stayed flat at 1.2%, and the engineering org completed the migration off the legacy queue system ahead of schedule.',
      keyAchievements:
        '- Closed 4 new enterprise logos, including our first Fortune 500 account\n- Shipped streaming inference API to general availability\n- Reduced infra spend 14% via GPU batching improvements\n- SOC 2 Type II audit completed with zero findings',
      productUpdates:
        '- **Streaming API** is now GA for all Pro and Enterprise tiers\n- Launched usage-based billing for the Insights add-on\n- Redesigned onboarding flow, reducing time-to-first-value from 18 min to 6 min',
      engineeringProgress:
        '- Completed migration from legacy job queue to the new event-driven pipeline\n- Cut P95 API latency from 340ms to 212ms\n- Rolled out canary deployments across all production services',
      aiInnovations:
        'Our research team validated a distillation approach that cuts model serving cost by ~30% with under 1% accuracy loss on internal benchmarks. Rollout to production is planned for next quarter pending A/B results.',
      customerSuccessStories:
        '**Northwind Logistics** reduced manual triage time by 62% after adopting PulseAI\'s classification pipeline, and renewed their contract at 2x seat count.',
      upcomingMilestones:
        '- Q3 kickoff and roadmap review — 2026-07-10\n- Distillation rollout A/B test — 2026-07-15\n- SOC 2 report distribution to enterprise prospects — 2026-07-20',
      risksChallenges:
        'GPU capacity in our primary region remains tight heading into Q3; we are evaluating a secondary region to de-risk availability during peak load.',
      decisionsRequired:
        'Leadership needs to approve budget for the secondary GPU region before the Q3 kickoff on 2026-07-10.',
      appendix: 'Full metrics dashboard and audit report available on request from the Data team.',
    },
  },
  {
    id: DRAFT_ID,
    title: 'PulseAI Monthly Pulse — Issue #13',
    issueNumber: 13,
    status: 'draft',
    periodStart: '2026-07-01',
    periodEnd: '2026-07-30',
    kpis: draftKpis,
    enabledSections: [...ALL_SECTION_ORDER],
    coverImage: undefined,
    currentVersion: 1,
    createdAt: '2026-07-28T10:00:00.000Z',
    updatedAt: '2026-07-30T11:05:00.000Z',
    publishedAt: undefined,
    sections: {
      executiveSummary:
        'July continued the momentum from June: MRR is trending toward $438K and uptime hit an all-time high of 99.97%. Draft in progress — pending final KPI numbers from Finance before publish.',
      keyAchievements:
        '- Closed 3 new mid-market accounts\n- Began secondary-region GPU rollout\n- Shipped v2 of the Insights dashboard',
      productUpdates: '- Insights dashboard v2 in private beta with 12 design partners',
      engineeringProgress: '- Secondary GPU region provisioning 40% complete',
      aiInnovations: 'Distillation A/B test is live; early results show a 28% cost reduction with no measurable quality regression.',
      customerSuccessStories: '',
      upcomingMilestones: '- Distillation A/B readout — 2026-08-05\n- Secondary region GA — 2026-08-15',
      risksChallenges: 'Secondary region rollout is tracking one week behind plan due to vendor hardware delays.',
      decisionsRequired: '',
      appendix: '',
    },
  },
];

export const SAMPLE_ACTION_ITEMS: ActionItem[] = [
  {
    id: 'ai-seed-2001',
    newsletterId: PUBLISHED_ID,
    title: 'Approve secondary GPU region budget',
    owner: 'Priya Nair (CFO)',
    dueDate: '2026-07-10',
    status: 'in_progress',
    createdAt: '2026-06-30T17:20:00.000Z',
    updatedAt: '2026-07-15T08:00:00.000Z',
  },
  {
    id: 'ai-seed-2002',
    newsletterId: PUBLISHED_ID,
    title: 'Publish SOC 2 report to enterprise prospect data room',
    owner: 'Marcus Webb',
    dueDate: '2026-07-05',
    status: 'completed',
    createdAt: '2026-06-30T17:20:00.000Z',
    updatedAt: '2026-07-04T12:00:00.000Z',
    completedAt: '2026-07-04T12:00:00.000Z',
  },
  {
    id: 'ai-seed-2003',
    newsletterId: DRAFT_ID,
    title: 'Approve secondary GPU region budget',
    owner: 'Priya Nair (CFO)',
    dueDate: '2026-08-01',
    status: 'in_progress',
    carriedFromId: 'ai-seed-2001',
    createdAt: '2026-07-28T10:00:00.000Z',
    updatedAt: '2026-07-28T10:00:00.000Z',
  },
  {
    id: 'ai-seed-2004',
    newsletterId: DRAFT_ID,
    title: 'Finalize July KPI numbers with Finance',
    owner: 'Dana Kim',
    dueDate: '2026-08-02',
    status: 'open',
    createdAt: '2026-07-28T10:00:00.000Z',
    updatedAt: '2026-07-28T10:00:00.000Z',
  },
];

export const SAMPLE_VERSIONS: NewsletterVersion[] = [
  {
    id: 'v-seed-3001',
    newsletterId: PUBLISHED_ID,
    versionNumber: 1,
    note: 'Initial publish',
    createdAt: '2026-06-30T17:20:00.000Z',
    snapshot: {
      title: SAMPLE_NEWSLETTERS[0].title,
      sections: SAMPLE_NEWSLETTERS[0].sections,
      kpis: SAMPLE_NEWSLETTERS[0].kpis,
      actionItemIds: ['ai-seed-2001', 'ai-seed-2002'],
      enabledSections: [...ALL_SECTION_ORDER],
    },
  },
];
